import { useState } from "react";
import { Link } from "react-router-dom";
import { ADVISOR_ONLY_ONE, AGENT_KINDS, createAgentBody } from "../lib/agent-kinds.ts";
import { type Agent, api } from "../lib/api.ts";
import { NEXT_PAGE, PREVIOUS_PAGE } from "../lib/table-copy.ts";
import { useLoad } from "../lib/useLoad.ts";
import { Button, InfoBanner, Input, Page, Panel, Select, Table } from "../ui/index.ts";

const STATUSES = [
	{ value: "active", label: "active" },
	{ value: "paused", label: "paused" },
	{ value: "retired", label: "retired" },
];

/** Rows per page of the list, in its panel. How many fit at 1280×800 is not measured. */
const AGENTS_PAGE = 10;

export function Agents() {
	const [agents, reload] = useLoad(() => api.agents());
	const [name, setName] = useState("");
	const [kind, setKind] = useState("buyer");
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	async function run(action: () => Promise<unknown>) {
		setBusy(true);
		setError(null);
		try {
			await action();
			reload();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	}

	async function create(e: React.FormEvent) {
		e.preventDefault();
		if (!name.trim()) return;
		setBusy(true);
		setError(null);
		try {
			await api.createAgent(createAgentBody(name, kind));
			setName("");
			reload();
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	}

	return (
		<Page
			fill
			title="Agents"
			description="The buying agents that act on the ad accounts. Access to each one is handed out on the Teams and Access pages."
		>
			<div className="flex h-full min-h-0 flex-col gap-3">
				{error && <InfoBanner variant="danger" title={error} />}
				{agents.state === "ready" && agents.data.truncated && (
					<InfoBanner variant="warning" title="This list is not complete.">
						More rows exist than one read returns, so what you see here is drawn from the newest
						1000, and {NEXT_PAGE} / {PREVIOUS_PAGE} move through those only. Nothing is missing from
						the database — the rest is not read until the read itself is paged.
					</InfoBanner>
				)}

				<Panel title="Agents">
					<Table<Agent>
						columns={[
							{
								id: "name",
								header: "Agent",
								accessor: (a) => (
									<>
										<Link to={`/agents/${a.id}`} className="font-medium text-content underline">
											{a.name}
										</Link>
										{a.description && <div className="text-xs text-muted">{a.description}</div>}
									</>
								),
							},
							{ id: "kind", header: "Kind", accessor: (a) => a.kind },
							{
								id: "status",
								header: "Status",
								accessor: (a) => (
									<Select
										value={a.status}
										disabled={busy}
										onChange={(e) =>
											void run(() => api.updateAgent(a.id, { status: e.target.value }))
										}
										options={STATUSES}
									/>
								),
							},
							{
								id: "id",
								header: "Id",
								accessor: (a) => <span className="font-mono text-xs text-muted">{a.id}</span>,
							},
							{
								id: "actions",
								header: "",
								accessor: (a) => (
									<Button
										variant="ghost"
										size="sm"
										disabled={busy}
										onClick={() => void run(() => api.deleteAgent(a.id))}
									>
										Delete
									</Button>
								),
							},
						]}
						data={agents.state === "ready" ? agents.data.agents : []}
						keyExtractor={(a) => a.id}
						failed={agents.state === "failed"}
						loading={agents.state === "loading"}
						pageSize={AGENTS_PAGE}
						dense
						emptyMessage="No agents yet. Add the first one below."
					/>
				</Panel>

				<Panel title="Add agent" className="flex-shrink-0">
					<form className="flex flex-wrap items-end gap-3" onSubmit={create}>
						<Input
							label="Name"
							placeholder="head-of-ads"
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
						<Select
							label="Kind"
							value={kind}
							onChange={(e) => setKind(e.target.value)}
							options={AGENT_KINDS}
						/>
						<Button type="submit" loading={busy} disabled={!name.trim()}>
							Add agent
						</Button>
					</form>
					{kind === "advisor" && <p className="mt-2 text-sm text-muted">{ADVISOR_ONLY_ONE}</p>}
				</Panel>
			</div>
		</Page>
	);
}
